/**
 * @wl/widget-mortgage — тексты выдачи банков (не только «Семейной»).
 *
 * Чистые функции «числа → строка», как и family-text.ts: index.ts кладёт результат
 * в DOM, формулировки проверяются тестами без браузера.
 */

import { formatPct } from './format';
import { pctText } from './family-text';
import type { Offer } from './offers';

const NBSP = '\u00a0';

/** Русская словоформа «предложение/предложения/предложений». */
export function offerWord(n: number): string {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return 'предложение';
  if (m10 >= 2 && m10 <= 4 && (m100 < 10 || m100 >= 20)) return 'предложения';
  return 'предложений';
}

/** «в 1 банке» / «в 3 банках» — предложный падеж после «в». */
export function banksInWord(n: number): string {
  return n % 10 === 1 && n % 100 !== 11 ? 'банке' : 'банках';
}

/**
 * Подпись ставки оффера.
 *
 *   • в пределах лимита — одно число, как и было: «10%»;
 *   • кредит выше лимита «Семейной» — обе ставки: «10 % + 16,99 %». Одно «10 %» тут
 *     было бы неправдой: на сумму сверх лимита банк берёт рыночную ставку, и
 *     покупатель, увидев 10 %, считал бы платёж по ней.
 */
export function offerRateText(o: Pick<Offer, 'rate' | 'family' | 'marketRate'>): string {
  if (o.family && o.family.marketLoan > 0 && typeof o.marketRate === 'number') {
    return `${pctText(o.rate)} + ${pctText(o.marketRate)}`;
  }
  return formatPct(o.rate);
}

/**
 * Заголовок сворачиваемого списка.
 *
 * Когда у ВСЕХ банков выдачи одна ставка и один платёж (бывает у «Семейной»: ставка
 * по сетке программы, а не у банка), «Ещё 3 предложения» обещает выбор, которого нет.
 * Честнее сказать, что условия те же: «Те же условия ещё в 3 банках».
 */
export function moreLabelText(restCount: number, allSame: boolean): string {
  return allSame
    ? `Те же условия ещё в${NBSP}${restCount}${NBSP}${banksInWord(restCount)}`
    : `Ещё ${restCount} ${offerWord(restCount)}`;
}

/** Одна ставка и один платёж (до рубля) у всех предложений выдачи. */
export function allOffersSame(offers: ReadonlyArray<Pick<Offer, 'rate' | 'payment'>>): boolean {
  const first = offers[0];
  if (!first) return false;
  return offers.every((o) => o.rate === first.rate && Math.round(o.payment) === Math.round(first.payment));
}

/**
 * «Металлинвестбанк не выдаёт сумму сверх лимита программы» — строка под списком о
 * банках, которых в выдаче нет из-за суммы кредита. Несколько банков — через
 * запятую, глагол во множественном числе. null — таких банков нет.
 */
export function droppedBanksText(names: readonly string[]): string | null {
  if (names.length === 0) return null;
  const verb = names.length === 1 ? 'не выдаёт' : 'не выдают';
  return `${names.join(', ')} ${verb} сумму сверх лимита программы`;
}
